import { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import axios from ".././axios-interceptor";
import {
  base_url,
  LOAN_CURRENCY_TYPE_USD,
  getInvestmentMinimum,
  getInvestmentStep,
  formatMoney,
  validateFundNowAmount,
  convertCurrency,
} from "../utilities";

// Tokens from the client-approved mockup (BenneFin-Investor-Dashboard-Preview/index.html).
const INK = "#292929";
const GRAYTEXT = "#7C8495";
const BORDER = "var(--ben-track)";
const BLUE = "var(--ben-blue)";
const GREEN = "#059669";
const RED = "#ED5151";

// Scoped to these two modals only (dialogClassName); width and backdrop tint follow the mockup.
const modalCss = `
.fn-dialog { max-width: 400px; }
.fn-dialog.fn-dialog-sm { max-width: 360px; }
.fn-dialog .modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 20px 50px rgba(13,29,84,.25); }
.fn-backdrop.show { background: rgb(13,29,84); opacity: .45; }
.fn-amount-input:focus { outline: none; border-color: ${BLUE} !important; background: #fff !important; }
`;

const headerStyle = {
  padding: "16px 20px",
  borderBottom: `1px solid ${BORDER}`,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const titleStyle = { margin: 0, fontSize: 15, fontWeight: 700, color: INK };
const closeBtnStyle = {
  background: "none",
  border: "none",
  fontSize: 20,
  color: GRAYTEXT,
  cursor: "pointer",
  lineHeight: 1,
};
const footerStyle = {
  padding: "14px 20px",
  borderTop: `1px solid ${BORDER}`,
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
};
const primaryBtnStyle = {
  background: BLUE,
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "9px 16px",
  fontSize: 12.5,
  fontWeight: 700,
};
const ghostBtnStyle = {
  background: "#fff",
  color: INK,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "9px 16px",
  fontSize: 12.5,
  fontWeight: 700,
};

// Minimum in the opportunity's currency (BHD 100; for USD loans the whole-dollar equivalent),
// same as the backend.
export const getMinimumInvestment = (currency, rate) => getInvestmentMinimum(currency, rate);

// The last pledge may take the whole remainder even when it is below the minimum.
export const getEffectiveMinimum = (currency, rate, remaining) => {
  const min = getMinimumInvestment(currency, rate);
  return remaining > 0 ? Math.min(min, remaining) : min;
};

const currencyLabel = (currency) => (currency === LOAN_CURRENCY_TYPE_USD ? "$" : "BHD");
const plainAmount = (amount, currency) =>
  `${currencyLabel(currency)}\u00A0${Number(amount || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

/**
 * Fund Now modal.
 * props:
 *  show, onHide
 *  opportunity      – the opportunity card row (investor_request_id, business_name, investor_roi, ...)
 *  remaining        – amount still to fund, in the opportunity currency
 *  availableBalance – investor's available balance converted into the opportunity currency
 *  rate             – USD per 1 BHD
 *  investorId       – from investor-master/get_investor_master_user
 *  onFunded(progress, amount, message) – called after a successful fund_now
 */
export function FundNowModal(props) {
  const { show, onHide, opportunity, remaining, availableBalance, walletBalance, rate, investorId, onFunded, investorCurrency } = props;
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (show) {
      setAmount("");
      setSubmitting(false);
    }
  }, [show, opportunity?.investor_request_id]);

  if (!opportunity) return null;

  const currency = opportunity.admin_request_loan_currency;
  const tenure = Number(opportunity.admin_request_loan_tenure) || 0;
  const roi = Number(opportunity.investor_roi) || 0;
  const businessName = opportunity.business_name || opportunity.business_type || "this opportunity";
  const effectiveMin = getEffectiveMinimum(currency, rate, remaining);
  const minimum = getMinimumInvestment(currency, rate);
  const step = getInvestmentStep(currency, rate);
  // When only a sub-minimum remainder is left, that exact amount is the only valid pledge.
  const onlyRemainder = remaining > 0 && remaining < minimum;

  const validation = validateFundNowAmount({
    amount,
    currency,
    remaining,
    availableBalance,
    rate,
  });
  const amt = Number(amount) || 0;
  // The loan amortizes (reducing balance), so interest shrinks every month: the total is NOT the
  // first month x tenure (QA #4: 12.34 x 5 = 61.70 vs the real 37.02). Use the opportunity's own
  // schedule (amount[].investor_profit, expected_return) scaled to this investment's share.
  const target = Number(opportunity.admin_request_loan_amount) || 0;
  const share = target > 0 ? amt / target : 0;
  const schedule = Array.isArray(opportunity.amount) ? opportunity.amount : [];
  const hasSchedule = share > 0 && schedule.length > 0 && opportunity.expected_return !== undefined;
  const round2 = (n) => Math.round(n * 100) / 100;
  const monthlyReturn = hasSchedule ? round2(Number(schedule[0].investor_profit || 0) * share) : (amt * roi) / 100;
  const totalReturn = hasSchedule ? round2(Number(opportunity.expected_return || 0) * share) : monthlyReturn * tenure;
  // A USD wallet funding a BHD opportunity (or vice versa) is debited in its own currency: show
  // the wallet amount and the rate so the conversion is never silent.
  const crossCurrency = !!investorCurrency && investorCurrency !== currency && Number(rate) > 0;

  const handleClose = () => {
    if (submitting) return;
    setAmount("");
    onHide();
  };

  const confirm = () => {
    if (!validation.ok || submitting) return;
    if (!investorId) {
      toast.error("Unable to identify your investor account. Please refresh and try again.", { toastId: 302 });
      return;
    }
    setSubmitting(true);
    axios
      .post(base_url + "loan-investor-subscriptions/fund_now", {
        investor_request_id: opportunity.investor_request_id,
        investor_id: investorId,
        amount: Number(amount),
      })
      .then((res) => {
        if (res?.data?.status === 200) {
          const progress = res.data.data?.progress;
          toast.success(res.data.message || "Investment confirmed", {
            toastId: "fund-" + opportunity.investor_request_id + "-" + Date.now(),
          });
          setSubmitting(false);
          setAmount("");
          onFunded && onFunded(progress, Number(amount), res.data.message);
        } else {
          // The interceptor has already shown the backend's message.
          setSubmitting(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setSubmitting(false);
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={submitting ? "static" : true}
      keyboard={!submitting}
      dialogClassName="fn-dialog"
      backdropClassName="fn-backdrop"
      onEntered={() => setTimeout(() => inputRef.current && inputRef.current.focus(), 50)}
    >
      <style>{modalCss}</style>
      <div style={headerStyle}>
        <h5 style={titleStyle}>Fund {businessName}</h5>
        <button type="button" style={closeBtnStyle} onClick={handleClose} disabled={submitting} aria-label="Close">
          &times;
        </button>
      </div>
      <div style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 12.5, color: GRAYTEXT, marginBottom: 14 }}>
          Your available balance: <b style={{ color: INK }}>{formatMoney(availableBalance, currency)}</b>
          {crossCurrency && (
            <div style={{ marginTop: 4 }}>
              = {formatMoney(
                walletBalance != null ? walletBalance : convertCurrency(availableBalance, currency, investorCurrency, rate),
                investorCurrency
              )}{" "}in your{" "}
              {investorCurrency} wallet, at {Number(rate)} USD per 1 BHD.
              {amt > 0 && (
                <>
                  {" "}This investment will debit{" "}
                  <b style={{ color: INK }}>{formatMoney(convertCurrency(amt, currency, investorCurrency, rate), investorCurrency)}</b>.
                </>
              )}
            </div>
          )}
        </div>
        <label
          htmlFor="fnAmountInput"
          style={{ display: "block", fontSize: 12, fontWeight: 700, color: INK, marginBottom: 6 }}
        >
          Amount to invest ({currency})
        </label>
        <div style={{ fontSize: 11.5, color: GRAYTEXT, marginTop: -2, marginBottom: 6 }}>
          {onlyRemainder
            ? `Only ${plainAmount(remaining, currency)} is left — invest exactly ${plainAmount(remaining, currency)}`
            : `In multiples of ${plainAmount(step, currency)}` +
              (remaining > 0 && Math.round(remaining * 100) % Math.round(step * 100) !== 0
                ? ` (or exactly the remaining ${plainAmount(remaining, currency)})`
                : "")}
        </div>
        <input
          id="fnAmountInput"
          ref={inputRef}
          className="fn-amount-input"
          type="number"
          inputMode="decimal"
          min={effectiveMin}
          step={onlyRemainder ? "any" : step}
          placeholder={`e.g. ${Number(effectiveMin).toLocaleString("en-US", { maximumFractionDigits: 2, useGrouping: false })}`}
          value={amount}
          disabled={submitting}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") confirm();
          }}
          style={{
            width: "100%",
            border: `1px solid ${BORDER}`,
            background: "#F2F3F3",
            borderRadius: 8,
            padding: "11px 13px",
            fontSize: 15,
            fontWeight: 700,
            color: INK,
          }}
        />
        <div
          style={{
            fontSize: 11.5,
            marginTop: 7,
            minHeight: 14,
            color: validation.ok ? GREEN : RED,
          }}
        >
          {validation.message}
        </div>
        {validation.ok && (
          <div
            style={{
              marginTop: 14,
              background: "#F7F7F5",
              border: `1px dashed ${BORDER}`,
              borderRadius: 10,
              padding: "12px 14px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 6 }}>
              <span>{hasSchedule ? "Estimated first-month return" : "Estimated monthly return"}</span>
              <b>{formatMoney(monthlyReturn, currency)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5 }}>
              <span>
                Estimated total return (over {tenure} {tenure === 1 ? "month" : "months"})
              </span>
              <b>{formatMoney(totalReturn, currency)}</b>
            </div>
            <div style={{ fontSize: 10.5, color: GRAYTEXT, marginTop: 8, lineHeight: 1.4 }}>
              {hasSchedule
                ? "Your pro-rata share of the loan's repayment schedule. The monthly return falls as the principal is repaid."
                : "Illustrative estimate at a flat monthly rate; actual payouts follow the amortized repayment schedule and your pro-rata share."}
            </div>
          </div>
        )}
      </div>
      <div style={footerStyle}>
        <button type="button" className="btn" style={ghostBtnStyle} onClick={handleClose} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className="btn"
          style={{ ...primaryBtnStyle, opacity: !validation.ok || submitting ? 0.5 : 1 }}
          onClick={confirm}
          disabled={!validation.ok || submitting}
        >
          {submitting ? "Processing..." : "Confirm Investment"}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Top-up required modal.
 * props: show, onHide, availableBalance, minimum, currency (all in the opportunity currency)
 */
export function TopUpModal({ show, onHide, availableBalance, minimum, currency }) {
  // Client copy: ask for a top-up of at least the minimum investment (no "by <shortfall>" amount).
  const minimumLabel = plainAmount(minimum, currency);
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="fn-dialog fn-dialog-sm"
      backdropClassName="fn-backdrop"
    >
      <style>{modalCss}</style>
      <div style={headerStyle}>
        <h5 style={titleStyle}>Top-up required</h5>
        <button type="button" style={closeBtnStyle} onClick={onHide} aria-label="Close">
          &times;
        </button>
      </div>
      <div style={{ padding: "18px 20px" }}>
        <p style={{ fontSize: 13, color: INK, lineHeight: 1.5, margin: 0 }}>
          Your available balance is below the minimum investment amount of {minimumLabel}. Kindly
          top up your BenneFin account with a minimum of {minimumLabel} to proceed with funding.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 12 }}>
          <tbody>
            <tr>
              <td style={{ padding: "7px 0" }}>Available Balance</td>
              <td style={{ padding: "7px 0", textAlign: "right" }}>{formatMoney(availableBalance, currency)}</td>
            </tr>
            <tr>
              <td style={{ padding: "7px 0", borderTop: `1px solid ${BORDER}` }}>Minimum Investment</td>
              <td style={{ padding: "7px 0", borderTop: `1px solid ${BORDER}`, textAlign: "right" }}>
                {formatMoney(minimum, currency)}
              </td>
            </tr>
            <tr style={{ fontWeight: 700 }}>
              <td style={{ padding: "7px 0", borderTop: `1px solid ${BORDER}` }}>Minimum Top-Up</td>
              <td style={{ padding: "7px 0", borderTop: `1px solid ${BORDER}`, textAlign: "right" }}>
                {formatMoney(minimum, currency)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div style={footerStyle}>
        <button type="button" className="btn" style={primaryBtnStyle} onClick={onHide}>
          Got it
        </button>
      </div>
    </Modal>
  );
}

export default FundNowModal;
